import { useRef, useState, useLayoutEffect, createContext, useMemo, useContext, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import type React from "react";
import cn from "mcn";

import { CloseIcon } from "../icons/CloseIcon";

type TModalTabsState = {
  tabs: { id: string; title: string }[];
  active: string;
  setActive: React.Dispatch<React.SetStateAction<string>>;
  setTabs: React.Dispatch<React.SetStateAction<{ id: string; title: string }[]>>;
};

const ModalTabsContext = createContext<TModalTabsState>({
  tabs: [],
  active: "",
  setActive: () => void 0,
  setTabs: () => void 0,
});

export const ModalTabs = ({
  children,
  active: propsActive,
  constrainRef,
  width,
  height,
  onClose,
}: {
  children: React.ReactNode;
  active: string;
  constrainRef: React.RefObject<HTMLDivElement>;
  width?: number;
  height?: number;
  onClose?: () => void;
}) => {
  "use no memo"; // FIXME: REACT-COMPILER - Error: Cannot access refs during render

  const { dragging, ref: dragRef, start: dragStart } = useDraggable({ constrainRef, position: "center" });
  const [tabs, setTabs] = useState<{ id: string; title: string }[]>([]);
  const [active, setActive] = useState(propsActive);

  const value = useMemo<TModalTabsState>(() => ({ tabs, active, setActive, setTabs }), [tabs, active]);

  return (
    <ModalTabsContext.Provider value={value}>
      <div
        ref={dragRef}
        data-testid="modal"
        className={cn(
          "rounded-lg border border-outline overflow-hidden select-none",
          "z-[30] bg-card absolute grid grid-rows-[min-content_1fr] max-w-full max-h-[90vh] ",
          "transition-[opacity,box-shadow] duration-100 [&_input]:select-text [&_textarea]:select-text",
          [dragging, "opacity-90"],
          [
            dragging,
            "dark:shadow-[5px_3px_7px_1px_rgba(100,100,100,0.25)]  shadow-[5px_3px_7px_1px_rgba(0,0,0,0.25)]",
            "dark:shadow-[4px_2px_6px_-1px_rgba(100,100,100,0.25)]  shadow-[4px_2px_6px_-1px_rgba(0,0,0,0.25)]",
          ],
        )}
        style={{
          width,
          height,
        }}
      >
        <div
          className={cn("flex w-full text-foreground items-center relative", tabs.length !== 1 ? "bg-card-muted" : "")}
        >
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              data-active={active === tab.id}
              data-fill={tabs.length === 1}
              onClick={() => setActive(tab.id)}
            >
              {tab.title}
            </TabButton>
          ))}
          {tabs.length !== 1 && <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-outline" />}
          <div
            className={cn(
              "h-full w-full",
              dragging ? "cursor-grabbing" : "cursor-grab",
              tabs.length === 1 ? "bg-transparent" : "bg-card-muted",
            )}
            onMouseDown={dragStart}
            onTouchStart={dragStart}
          />
          {onClose && (
            <div className="pr-2">
              <CloseIcon onClick={onClose} />
            </div>
          )}
        </div>
        {children}
      </div>
    </ModalTabsContext.Provider>
  );
};

export const ModalTab = ({ children, title, id }: { children: React.ReactNode; title: string; id: string }) => {
  const { setTabs, active } = useContext(ModalTabsContext);

  useLayoutEffect(() => {
    setTabs((x) => (x.some((y) => y.id === id) ? x : [...x, { id, title: title }]));
    return () => setTabs((x) => x.filter((y) => y.id !== id));
  }, [id, title, setTabs]);

  if (active !== id) return null;
  return children;
};

export const TabButton = (
  p: React.ComponentProps<"button"> & {
    "data-active"?: boolean;
    "data-fill"?: boolean;
  },
) => (
  <button
    type="button"
    {...p}
    className={twMerge(
      "flex-1 h-full px-6 py-3 text-nowrap data-[fill=false]:border-r data-[fill=false]:border-outline data-[fill=false]:last:border-r-0 relative data-[active=true]:bg-card data-[active=true]:text-foreground data-[active=false]:bg-card-muted data-[active=false]:text-foreground-muted data-[fill=true]:text-xl data-[fill=true]:font-bold data-[active=true]:data-[fill=false]:after:content-[''] data-[active=true]:data-[fill=false]:after:absolute data-[active=true]:data-[fill=false]:after:bottom-0 data-[active=true]:data-[fill=false]:after:left-0 data-[active=true]:data-[fill=false]:after:right-0 data-[active=true]:data-[fill=false]:after:h-[2px] data-[active=true]:data-[fill=false]:after:bg-card data-[active=true]:data-[fill=false]:after:z-10",
      p.className,
    )}
  />
);

// --- useDraggable hook -----------------------------------------------------
const useDraggable = ({
  constrainRef,
  position,
}: {
  constrainRef: React.RefObject<HTMLDivElement>;
  position?: "center";
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const stateRef = useRef({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  // start drag
  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setDragging(true);
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    const parentRect = constrainRef.current?.getBoundingClientRect();
    if (!parentRect) return;
    stateRef.current = {
      x: clientX - offsetRef.current.x - parentRect.left,
      y: clientY - offsetRef.current.y - parentRect.top,
    };
  };

  const move = useCallback(() => {
    if (!elementRef) return;
    const bounding = constrainRef.current?.getBoundingClientRect();
    if (elementRef.current !== null) {
      elementRef.current.style.left = `${(bounding?.left ?? 0) + offsetRef.current.x}px`;
      elementRef.current.style.top = `${(bounding?.top ?? 0) + offsetRef.current.y}px`;
    }
  }, [constrainRef]);

  const update = useCallback(
    (x: number, y: number) => {
      offsetRef.current = { x: x, y: y };
      move();
    },
    [move],
  );

  // handle move & up
  useLayoutEffect(() => {
    if (!dragging) return;

    const onMove = (e: MouseEvent | TouchEvent) => {
      const pos =
        e instanceof TouchEvent ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };

      if (elementRef.current !== null) {
        const parentRect = constrainRef.current?.getBoundingClientRect();
        const modalRect = elementRef.current.getBoundingClientRect();
        if (!parentRect || !modalRect) return;

        let newX = pos.x - stateRef.current.x - parentRect.left;
        let newY = pos.y - stateRef.current.y - parentRect.top;

        // constrain within
        newX = Math.max(0, Math.min(newX, parentRect.width - modalRect.width));
        newY = Math.max(0, Math.min(newY, parentRect.height - modalRect.height));

        update(newX, newY);
      }
    };

    const onUp = () => setDragging(false);

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, update, constrainRef]);

  // on mount: center if needed
  useLayoutEffect(() => {
    if (elementRef.current == null || !constrainRef.current || !position) return;
    const parentRect = constrainRef.current.getBoundingClientRect();
    const modalRect = elementRef.current.getBoundingClientRect();
    update((parentRect.width - modalRect.width) / 2, (parentRect.height - modalRect.height) / 2);
  }, [position, constrainRef, update]);

  useLayoutEffect(() => {
    const parent = constrainRef.current;
    if (!parent) return;
    const observer = new ResizeObserver(() => {
      if (elementRef.current === null) return;
      const parentRect = parent.getBoundingClientRect();
      const modalRect = elementRef.current.getBoundingClientRect();
      const { x, y } = offsetRef.current;
      const nx = Math.max(0, Math.min(x, parentRect.width - modalRect.width));
      const ny = Math.max(0, Math.min(y, parentRect.height - modalRect.height));
      update(nx, ny);
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, [constrainRef, update]);

  return { dragging, ref: elementRef, start };
};
